/**
 * Shared descriptor for the Production Issues page tab strip.
 *
 * Single source of truth for the tab order, i18n label keys, and icons, consumed
 * by both the tab strip (`IssuesPage`) and the show/hide settings section
 * (`IssueTabsSettingsSection`) so the two never drift.
 *
 * Order here is the canonical render order of the tabs.
 */
import {
  PackageX,
  Pause,
  GitBranch,
  RotateCcw,
  Archive,
  XCircle,
  Truck,
  FileWarning,
  FileEdit,
  PackageSearch,
  type LucideIcon,
} from 'lucide-react';
import type { TabValue } from './types';

export interface IssueTabDescriptor {
  value: TabValue;
  /** i18n key in the `production` namespace (reused by the tab trigger + settings row). */
  labelKey: string;
  icon: LucideIcon;
}

export const ISSUE_TABS: ReadonlyArray<IssueTabDescriptor> = [
  { value: 'oos', labelKey: 'issues.tabs.oos', icon: PackageX },
  { value: 'on-hold', labelKey: 'issues.tabs.onHold', icon: Pause },
  { value: 'split-orders', labelKey: 'issues.tabs.split', icon: GitBranch },
  { value: 'redos', labelKey: 'issues.tabs.redos', icon: RotateCcw },
  { value: 'stuck-bins', labelKey: 'issues.tabs.stuckBins', icon: Archive },
  { value: 'cancelled', labelKey: 'issues.tabs.cancelled', icon: XCircle },
  { value: 'shipping', labelKey: 'issues.tabs.shipping', icon: Truck },
  { value: 'print-files', labelKey: 'issues.tabs.printFiles', icon: FileWarning },
  { value: 'order-changes', labelKey: 'issues.tabs.orderChanges', icon: FileEdit },
  { value: 'catalog', labelKey: 'issues.tabs.catalog', icon: PackageSearch },
];

/**
 * Tabs hidden by default for a PURE CLIENT org at a 3PL floor.
 *
 * A client's own rows legitimately include issues raised by the OWNER's floor
 * operators — `catcher.dao.ts` deliberately stamps them with the client's org
 * ("the issue belongs in the client's issue queue"), so they are correctly
 * scoped, not leaked. But a redo or a stuck bin is the 3PL's problem to action;
 * a client can only be confused by them.
 *
 * `cancelled` is here for the same reason and is easy to misread: the tab lists
 * physical GARMENTS stranded on the floor by a cancelled order (unprocessed
 * units + 30-day-old cancelled units still at the location), not cancelled
 * orders. Only the floor can action those; a client tracks cancellation status
 * from its Orders inbox instead.
 *
 * `order-changes` likewise: the pane's own copy is "review an order to see
 * exactly what needs cancelling or making, then fix it here" — reconciling
 * in-flight production against a changed order is floor work. The client
 * already knows its order changed; it sees the outcome in its Orders inbox.
 *
 * NOISE FILTER, NOT A BOUNDARY. This is a render-time default: the tab strip is
 * client-side and the underlying rows remain readable by the API. Nothing here
 * should ever be described as "clients cannot see redos" — if that becomes a
 * requirement it needs a server-side issue_type allowlist, not this list.
 * Chosen deliberately over an allowlist for now because the rows are already
 * correctly scoped, so this is about noise, and it is reversible.
 */
export const CLIENT_HIDDEN_ISSUE_TABS: readonly TabValue[] = [
  'redos',
  'stuck-bins',
  'cancelled',
  'order-changes',
];

/**
 * Tabs hidden for the CX role — same noise-filter rationale as
 * {@link CLIENT_HIDDEN_ISSUE_TABS} (render-time default, NOT a boundary): redos,
 * stuck bins and cancellation clean-up stay floor work.
 *
 * `order-changes` is NOT here (BLU-1257): CX now reconciles an order change on
 * its own org's orders (top up the missing units, cancel the surplus ones,
 * accept as-is), gated on `orders.resolve_changes`. A CX user at a PURE CLIENT
 * org still loses the tab through {@link CLIENT_HIDDEN_ISSUE_TABS}, which is
 * unchanged.
 */
export const CX_HIDDEN_ISSUE_TABS: readonly TabValue[] = [
  'redos',
  'stuck-bins',
  'cancelled',
];

/**
 * Drop the floor-only tabs for a pure client and/or the CX role.
 *
 * Applied AFTER {@link computeRenderedIssueTabs}, never folded into its
 * `hiddenTabs` argument — that helper deliberately REVEALS a hidden tab when it
 * is the `?tab=` deep-link target, which would let `?tab=redos` undo this.
 * Order is preserved; a non-client non-CX caller is returned untouched.
 */
export function applyClientTabVisibility(
  tabs: IssueTabDescriptor[],
  isPureClient: boolean,
  isCX = false,
): IssueTabDescriptor[] {
  if (!isPureClient && !isCX) return tabs;
  return tabs.filter(
    (tab) =>
      !(isPureClient && CLIENT_HIDDEN_ISSUE_TABS.includes(tab.value)) &&
      !(isCX && CX_HIDDEN_ISSUE_TABS.includes(tab.value)),
  );
}

/** All tab values in canonical order — used to validate `?tab=` deep-links. */
export const ALL_ISSUE_TAB_VALUES: readonly TabValue[] = ISSUE_TABS.map((t) => t.value);

/**
 * Tabs to render given the user's hidden set and the active `?tab=` deep-link.
 *
 * A hidden tab that is the current deep-link target is revealed for the session
 * so resolver/alert return paths (e.g. `?tab=split-orders`) never land on an
 * empty pane. Result is never empty: if every tab is hidden (and none is the
 * deep-link target) it falls back to all tabs.
 */
export function computeRenderedIssueTabs(
  hiddenTabs: readonly TabValue[],
  tabParam: string | null,
): IssueTabDescriptor[] {
  const list = ISSUE_TABS.filter(
    (tab) => !hiddenTabs.includes(tab.value) || tab.value === tabParam,
  );
  return list.length > 0 ? list : [...ISSUE_TABS];
}

/** The active tab if it is currently rendered, otherwise the first rendered tab. */
export function resolveEffectiveIssueTab(
  rendered: readonly IssueTabDescriptor[],
  activeTab: TabValue,
): TabValue {
  return rendered.some((tab) => tab.value === activeTab)
    ? activeTab
    : (rendered[0]?.value ?? 'oos');
}

/**
 * Add/remove a tab from the hidden denylist.
 * `visible === false` hides the tab; `true` shows it (removes from the list).
 */
export function toggleHiddenIssueTab(
  hiddenTabs: readonly TabValue[],
  tab: TabValue,
  visible: boolean,
): TabValue[] {
  return visible ? hiddenTabs.filter((v) => v !== tab) : [...hiddenTabs, tab];
}

/**
 * The numeric count fields the tab badges read, keyed by backend field name.
 *
 * Structural on purpose: both the tRPC-inferred `getCounts` payload (which
 * carries extra fields like `pap_damaged`) and the Dashboard's mapped
 * `getDashboardStats` subset satisfy it without re-annotating either.
 * All fields optional — a missing/loading payload counts as 0.
 */
export interface IssueTabBadgeCounts {
  oos_order_count?: number;
  on_hold_count?: number;
  split_orders?: number;
  redo_pending?: number;
  stuck_bins?: number;
  cancelled_count?: number;
  print_file_issues?: number;
  /** BLU-1439: OPEN print-file rows automation has given up on (needs a
   *  person). The print-files badge counts these plus the awaiting-bridge
   *  rows; auto-fetching rows are on their way and stay quiet. */
  print_file_needs_manual?: number;
  /** BLU-1439: OPEN print-file rows the bridge queue is holding
   *  (`awaiting_bridge`: a platen variant file is needed, outputs already
   *  present, storage not bridgeable, a missing source side ...). Most of
   *  these need a person, so the badge counts them. */
  print_file_awaiting_bridge?: number;
  order_changed?: number;
  sku_not_found?: number;
}

/**
 * BLU-1439 (D-Q1): the print-files badge counts the rows that need a person:
 * needs-manual plus awaiting-bridge (a held row, e.g. a platen variant file
 * needed or outputs already present, waits on a person). Only the rows the
 * bridge is actively auto-fetching stay uncounted. Falls back to every OPEN
 * row for an older backend payload that has no `print_file_needs_manual`
 * field.
 */
export function printFileBadgeCount(counts: IssueTabBadgeCounts | undefined): number {
  if (counts?.print_file_needs_manual == null) return counts?.print_file_issues ?? 0;
  return counts.print_file_needs_manual + (counts.print_file_awaiting_bridge ?? 0);
}

/**
 * The Dashboard's print-files number: the badge count from the cached
 * issue counts the sidebar already fetches, so the Dashboard alert equals the
 * tab badge. While those counts load, the dashboard-stats number (every OPEN
 * row) stands in.
 */
export function dashboardPrintFileCount(
  counts: IssueTabBadgeCounts | undefined,
  statsPrintFileIssues: number | undefined,
): number {
  return counts ? printFileBadgeCount(counts) : (statsPrintFileIssues ?? 0);
}

/**
 * Canonical per-tab badge count — the single mapping from a tab to its count
 * field, shared by the tab strip badges and the aggregate totals (sidebar nav
 * badge, Dashboard issues alert) so the badge and the totals can never drift.
 * Shipping failures and Portless-blocked orders come from separate queries
 * (`useShippingFailures` / `usePortlessBlockedCount`), hence the explicit
 * params — both required, so a caller that forgets one fails to compile
 * instead of silently undercounting the Shipping tab (BLU-732).
 */
export function issueTabBadgeCount(
  value: TabValue,
  counts: IssueTabBadgeCounts | undefined,
  shippingUnresolved: number,
  portlessBlocked: number,
): number {
  switch (value) {
    case 'oos':
      return counts?.oos_order_count ?? 0;
    case 'on-hold':
      return counts?.on_hold_count ?? 0;
    case 'split-orders':
      return counts?.split_orders ?? 0;
    case 'redos':
      return counts?.redo_pending ?? 0;
    case 'stuck-bins':
      return counts?.stuck_bins ?? 0;
    case 'cancelled':
      return counts?.cancelled_count ?? 0;
    case 'shipping':
      // Both states of "why can't this parcel leave": failures at the ship
      // step + orders Portless hasn't labelled yet (BLU-713 blocked list).
      return shippingUnresolved + portlessBlocked;
    case 'print-files':
      return printFileBadgeCount(counts);
    case 'order-changes':
      return counts?.order_changed ?? 0;
    case 'catalog':
      return counts?.sku_not_found ?? 0;
    default:
      return 0;
  }
}

/**
 * Total issue count across the tabs the user has NOT hidden — the sum of the
 * visible tab badges. Keys off the raw `hiddenIssueTabs` denylist (not
 * `computeRenderedIssueTabs`) so a temporary `?tab=` deep-link reveal never
 * inflates the persistent totals shown in the sidebar/Dashboard.
 */
export function computeVisibleIssueTotal(
  counts: IssueTabBadgeCounts | undefined,
  shippingUnresolved: number,
  hiddenTabs: readonly TabValue[],
  portlessBlocked: number,
): number {
  return ISSUE_TABS.filter((tab) => !hiddenTabs.includes(tab.value)).reduce(
    (sum, tab) => sum + issueTabBadgeCount(tab.value, counts, shippingUnresolved, portlessBlocked),
    0,
  );
}
